import classNames from 'classnames';
import React, { useMemo } from 'react';
import { AsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDEmptyState from 'ud-ui/components/empty-state';

import UDPagedListPager from './components/paged-list-pager';
import UDPagedListSkeleton from './components/paged-list-skeleton';
import UDPagedListToolbar from './components/paged-list-toolbar';

const DEFAULT_EMPTY_TEXT = 'Список пуст';
const DEFAULT_NOT_FOUND_TEXT = 'Ничего не найдено';
const DEFAULT_ERROR_TEXT = 'Не удалось загрузить список';

export interface UDPagedListProps<T> {
  /** Элементы текущей страницы. Резать их компонент не будет — это работа хука. */
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Ключ строки. По умолчанию — позиция в списке. */
  getItemKey?: (item: T, index: number) => React.Key;

  /** Какую ветку рисовать; `isRefreshing` притушивает уже показанное. */
  state: AsyncState;

  page: number;
  /** Наибольший известный номер страницы. При `<= 1` пагинатор скрыт. */
  totalPages: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  /** Адрес страницы списка с другим номером — для `<a href>` в пагинаторе. */
  pageHref?: (page: number) => string;

  /** Значение поля поиска (черновик из `usePagedList`). */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Дополнительные фильтры страницы, рисуются в тулбаре. */
  extraFilters?: React.ReactNode;

  /** Текст, когда список пуст сам по себе. */
  emptyText?: React.ReactNode;
  emptySubtitle?: React.ReactNode;
  /** Кнопка под текстом пустого состояния — обычно «Добавить …». */
  emptyAction?: React.ReactNode;
  /** Текст, когда пуст результат поиска. */
  notFoundText?: React.ReactNode;
  errorText?: React.ReactNode;

  className?: string;
  listClassName?: string;
  /**
   * Раскладка самих элементов. По умолчанию — колонка строк; страница с
   * карточками передаёт свою сетку. Значение **заменяет** дефолт, а не
   * дописывается к нему: `display` от двух классов сразу не складывается.
   */
  itemsClassName?: string;
  /** Высота одной заглушки скелетона: у строки и у карточки она разная. */
  skeletonRowHeight?: number;
}

/**
 * Страница-категория: тулбар, список, пагинатор.
 *
 * Компонент **чисто презентационный**: он не знает ни про URL, ни про RTK Query,
 * ни про роуты — всё приходит пропсами, ровно в том виде, в каком это отдаёт
 * `usePagedList`. Так `ud-ui` остаётся домен-независимым набором (правило
 * «`ud-ui` не импортирует фичи» из `.claude/rules/architecture.md`).
 *
 * Строку рисует сама страница через `renderItem` — компонент не принимает готовые
 * элементы `children`, как это делал `CategoryListPage`: там страница была обязана
 * отрендерить все накопленные элементы, а компонент резал уже готовые узлы.
 *
 * Ветку рендера выбирает только `state.status`; «идёт запрос» само по себе список
 * не стирает — скелетон показывается лишь на первой загрузке.
 */
function UDPagedList<T>({
  items,
  renderItem,
  getItemKey,
  state,
  page,
  totalPages,
  perPage,
  onPageChange,
  onPerPageChange,
  pageHref,
  search,
  onSearchChange,
  searchPlaceholder,
  extraFilters,
  emptyText = DEFAULT_EMPTY_TEXT,
  emptySubtitle,
  emptyAction,
  notFoundText = DEFAULT_NOT_FOUND_TEXT,
  errorText = DEFAULT_ERROR_TEXT,
  className,
  listClassName,
  itemsClassName = 'flex flex-col gap-2.5',
  skeletonRowHeight,
}: UDPagedListProps<T>) {
  const isSearching = search.trim().length > 0;

  const empty = useMemo(
    () =>
      isSearching ? (
        // При поиске кнопка создания не предлагается: пусто не потому, что
        // ничего нет, а потому, что не совпало с запросом.
        <UDEmptyState title={notFoundText} />
      ) : (
        <UDEmptyState title={emptyText} subtitle={emptySubtitle} action={emptyAction} />
      ),
    [isSearching, notFoundText, emptyText, emptySubtitle, emptyAction],
  );

  return (
    <div className={classNames('flex h-full flex-col px-6 pt-6', className)}>
      <UDPagedListToolbar
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        // Ветка `loading` при непустом поиске и есть «запрос в пути»; когда список
        // уже на экране, о том же говорит `isRefreshing`.
        isSearchLoading={state.isRefreshing || state.status === 'loading'}
        perPage={perPage}
        onPerPageChange={onPerPageChange}
        extraFilters={extraFilters}
      />

      <UDAsyncContent
        state={state}
        data-testid="paged-list-content"
        className={classNames('flex-1', listClassName)}
        loading={<UDPagedListSkeleton count={perPage} className={itemsClassName} rowHeight={skeletonRowHeight} />}
        empty={empty}
        error={<UDEmptyState title={errorText} />}
      >
        <div className={itemsClassName} role="list">
          {items.map((item, index) => (
            <React.Fragment key={getItemKey ? getItemKey(item, index) : index}>{renderItem(item, index)}</React.Fragment>
          ))}
        </div>
      </UDAsyncContent>

      <UDPagedListPager
        className="mt-auto py-4"
        totalPages={totalPages}
        currentPage={page}
        onPageChange={onPageChange}
        pageHref={pageHref}
      />
    </div>
  );
}

export default UDPagedList;
